import type { HourRange, WeekdayCategorySeries } from "@ii-gp-widget/shared/browser";

/** Below this many hourly history points the week is too thin to define "busy" from. */
const MIN_HISTORY_POINTS = 24;
/** A gym whose busiest hour is a couple of people should not paint one visitor as the max. */
const MIN_SCALE = 4;

export type ForecastScale = { value: number; basis: "history" | "capacity" };

/**
 * What the curves treat as their top line: the premise's own busiest hour — the largest
 * summed visitor count in any hour of its week of history. Labelled "max · N" in the widget,
 * so it has to be literally that — a smoothed figure such as a percentile would read as a number
 * the gym never reached, with the curve still hitting the ceiling on ordinary busy hours. The
 * API's `estimated_capacity` is not used when history exists: it is a theoretical maximum no
 * premise gets near (23–49 % of it at the busiest recorded hour on every premise checked), and
 * would flatten the curve along the bottom. It remains the fallback when history is too thin.
 *
 * `hours` narrows it to the hours the widget actually draws, so the "max" label describes the
 * plot in front of the reader rather than an hour that is hidden.
 */
export function computeForecastScale(
  weekly: Record<string, WeekdayCategorySeries[]> | undefined,
  capacity: number | null | undefined,
  hours?: HourRange | null,
): ForecastScale | null {
  const totalsByHour = new Map<string, number>();
  for (const [weekday, series] of Object.entries(weekly ?? {})) {
    for (const s of series) {
      for (const h of s.hours) {
        if (hours && (h.hour < hours.start || h.hour >= hours.end)) continue;
        const key = `${weekday}:${h.hour}`;
        totalsByHour.set(key, (totalsByHour.get(key) ?? 0) + h.objectCount);
      }
    }
  }

  const values = [...totalsByHour.values()];
  if (values.length >= MIN_HISTORY_POINTS) {
    return { value: Math.max(MIN_SCALE, Math.max(...values)), basis: "history" };
  }
  if (capacity && capacity > 0) return { value: capacity, basis: "capacity" };
  return null;
}
